import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePublicEchoesQuery } from '@/hooks/usePublicEchoesQuery';
import { useMyEchoesQuery } from '@/hooks/useMyEchoesQuery';
import { useMyDraftsQuery } from '@/hooks/useMyDraftsQuery';
import { queryKeys } from '@/libs/queryKeys';
import type { EchoProfileResponse, EchoDraftResponse } from '@/types/echo_creation';
import type { Echo } from '@/types';
import echoCreatedImage from '@/assets/images/webp/echo_created.webp';
import { getFullImageUrl } from '@/utils/url';

interface EchoContextType {
  // Data
  myEchoes: EchoProfileResponse[];
  myDrafts: EchoDraftResponse[];
  // Unified map of all available echoes (Public + Created)
  allContactEchoes: Record<string, Echo>;
  // Lists for easier consumption (public feed)
  publicEchoesList: Echo[];

  // State
  isLoading: boolean;

  // Actions
  refreshEchoes: () => Promise<void>;
  /**
   * Instantly clears local echo data.
   * Used when deleting a creator profile to update UI without refresh.
   */
  clearEchoData: () => void;
}

const EchoContext = React.createContext<EchoContextType | undefined>(undefined);

/**
 * EchoProvider Component
 *
 * Manages the global state for the user's created Echoes and Drafts.
 * Merges server-side user echoes with client-side system echoes for a unified contact list.
 * Refactored to use TanStack Query for data fetching and caching.
 */
export const EchoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();

  // 1. Consume Queries
  const {
    data: publicEchoesList = [],
    isLoading: isPublicLoading,
    refetch: refetchPublic,
  } = usePublicEchoesQuery();

  const {
    data: myEchoes = [],
    isLoading: isMyEchoesLoading,
    refetch: refetchMyEchoes,
  } = useMyEchoesQuery();

  const {
    data: myDrafts = [],
    isLoading: isMyDraftsLoading,
    refetch: refetchMyDrafts,
  } = useMyDraftsQuery();

  const isLoading = isPublicLoading || isMyEchoesLoading || isMyDraftsLoading;

  // 2. Actions
  const refreshEchoes = React.useCallback(async () => {
    // Parallel refetch of all relevant data
    await Promise.all([refetchPublic(), refetchMyEchoes(), refetchMyDrafts()]);
  }, [refetchPublic, refetchMyEchoes, refetchMyDrafts]);

  const clearEchoData = React.useCallback(() => {
    // Manually clear the cache for user-specific data
    queryClient.setQueryData(queryKeys.echoes.mine, []);
    queryClient.setQueryData(queryKeys.echoes.drafts, []);
  }, [queryClient]);

  // 3. Derived State: Merge Public Echoes + User Echoes for Chat/Contacts
  const allContactEchoes = React.useMemo(() => {
    const dynamicEchoes: Record<string, Echo> = {};

    // A. Add Public Echoes
    publicEchoesList.forEach((e) => {
      dynamicEchoes[e.id] = e;
    });

    // B. Add/Override with My Echoes (prefer user's own echo profile)
    myEchoes.forEach((e) => {
      dynamicEchoes[e.id] = {
        id: e.id,
        name: e.name || 'My Echo',
        category: e.category || 'Personal',
        domain: e.domain || 'General',
        imageUrl: getFullImageUrl(e.profile_image_url) ?? echoCreatedImage,
        videoAvatarUrl: e.video_avatar_url ? (getFullImageUrl(e.video_avatar_url) ?? null) : null,
        videoMessagesEnabled: e.video_messages_enabled ?? false,
        idleVideoUrl: e.idle_video_url ? (getFullImageUrl(e.idle_video_url) ?? null) : null,
      };
    });

    return dynamicEchoes;
  }, [myEchoes, publicEchoesList]);

  const value = React.useMemo(
    () => ({
      myEchoes,
      myDrafts,
      allContactEchoes,
      publicEchoesList,
      isLoading,
      refreshEchoes,
      clearEchoData,
    }),
    [
      myEchoes,
      myDrafts,
      allContactEchoes,
      publicEchoesList,
      isLoading,
      refreshEchoes,
      clearEchoData,
    ]
  );

  return <EchoContext.Provider value={value}>{children}</EchoContext.Provider>;
};

/**
 * Hook to access the EchoContext.
 * Must be used within an EchoProvider.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useEchoStore = () => {
  const context = React.useContext(EchoContext);
  if (!context) {
    throw new Error('useEchoStore must be used within an EchoProvider');
  }
  return context;
};
